/*  HeroImageUploader — inline uploader shown ONLY when the admin is
    editing a product page's `hero.image_url` CMS entry.

    Behavior:
      • Shows a thumbnail of whatever URL is currently in the field
        (either the seeded default, or a previously uploaded custom).
      • "Upload new image" opens a native file picker.
      • On file select, POSTs to /api/admin/landing/hero-image with the
        page slug (product_secure/serene/apex/infinite) → backend stores
        the file in Emergent Object Storage → returns a canonical public
        URL under /api/marketing/hero/{id} → we drop that URL into the
        textarea via `onChange` so the standard "Save changes" bar
        picks it up like any other CMS edit.
      • Validates MIME + size client-side to fail fast (the backend
        also validates — this is just a UX shortcut).

    The full public URL is stored in the CMS (prefixed by
    REACT_APP_BACKEND_URL) so image src="…" resolves through Kubernetes
    ingress without needing a relative-path shim.
*/
import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadSimple, Image as ImageIcon, Warning, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";


const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_MB = 10;


/**
 * @param {object} props
 * @param {string} props.page      — CMS page slug (product_secure|serene|apex|infinite).
 * @param {string} props.value     — current image URL (draft value in the parent).
 * @param {function} props.onChange — (newUrl:string) => void, called when the upload succeeds.
 */
export default function HeroImageUploader({ page, value, onChange }) {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const openPicker = () => fileInputRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // Reset so re-selecting the same file re-triggers.
        if (!file) return;
        if (!ALLOWED_MIME.includes(file.type)) {
            toast.error(`Unsupported type: ${file.type}. Use JPEG, PNG, WebP, or GIF.`);
            return;
        }
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`File too large. Max ${MAX_MB} MB.`);
            return;
        }

        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("page", page);
            const res = await api.post("/admin/landing/hero-image", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // The backend returns a path-only URL. Resolve to a full URL
            // against REACT_APP_BACKEND_URL so the value stored in CMS is
            // portable across environments.
            const relativeUrl = res.data?.url;
            if (!relativeUrl) throw new Error("Empty response from server.");
            const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
            const fullUrl = relativeUrl.startsWith("http") ? relativeUrl : `${backend}${relativeUrl}`;
            onChange(fullUrl);
            toast.success("Image uploaded. Click Save changes to publish.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message || "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    // Only recognised page slugs get an uploader. Guards against admin
    // switching to a non-product page with a `hero.image_url` key.
    const isSupported = ["product_secure", "product_serene", "product_apex", "product_infinite"].includes(page);

    return (
        <div className="mt-2 flex items-start gap-3 p-3 rounded-xl bg-primary/[0.04] border border-primary/15">
            {/* Live preview */}
            <div className="w-24 h-24 rounded-lg bg-secondary/40 border border-border overflow-hidden shrink-0 grid place-items-center">
                {value ? (
                    <img
                        src={value}
                        alt="Current hero"
                        className="w-full h-full object-cover"
                        data-testid="hero-uploader-preview"
                        onError={(e) => {
                            // Fallback icon when the URL 404s (broken CMS entry).
                            e.currentTarget.style.display = "none";
                        }}
                    />
                ) : (
                    <ImageIcon size={28} weight="duotone" className="text-muted-foreground" />
                )}
            </div>

            <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-foreground mb-0.5 flex items-center gap-1.5">
                    <ImageIcon size={12} weight="fill" className="text-primary" />
                    Hero image uploader
                </div>
                <div className="text-[10.5px] text-muted-foreground leading-snug mb-2">
                    JPEG · PNG · WebP · GIF · Max {MAX_MB} MB. The URL below updates automatically.
                </div>
                {!isSupported ? (
                    <div className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-amber-700">
                        <Warning size={12} weight="fill" />
                        Uploader available only on product pages.
                    </div>
                ) : (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={openPicker}
                        disabled={uploading}
                        className="h-8 px-3 text-xs"
                        data-testid="hero-uploader-button"
                    >
                        {uploading ? (
                            <>
                                <UploadSimple size={12} weight="bold" className="mr-1.5 animate-pulse" />
                                Uploading…
                            </>
                        ) : (
                            <>
                                <UploadSimple size={12} weight="bold" className="mr-1.5" />
                                Upload new image
                            </>
                        )}
                    </Button>
                )}
                {value && !uploading && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-emerald-600 font-semibold">
                        <CheckCircle size={11} weight="fill" />
                        URL populated
                    </span>
                )}
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_MIME.join(",")}
                onChange={handleFile}
                className="hidden"
                data-testid="hero-uploader-input"
            />
        </div>
    );
}
